
import { useCallback, useMemo } from 'react';
import { Exercise } from '@/models/LearnTypes';

interface UseRecyclingPhaseProps {
  exercises: Exercise[];
  currentExerciseIndex: number;
  totalOriginalExercises: number;
  wordsToReview: string[];
  currentExerciseId?: string;
}

interface UseRecyclingPhaseReturn {
  isInRecyclingPhase: boolean;
  calculateTotalOriginalExercises: () => number;
}

/**
 * Hook to determine if we're tracking failed words (no longer recycling)
 */
export const useRecyclingPhase = ({
  exercises,
  currentExerciseIndex,
  totalOriginalExercises,
  wordsToReview,
  currentExerciseId
}: UseRecyclingPhaseProps): UseRecyclingPhaseReturn => {
  
  // Calculate the number of original exercises
  const calculateTotalOriginalExercises = useCallback(() => {
    console.log(`🔍 [useRecyclingPhase] Calculating original exercises count for ${exercises.length} exercises`);
    
    const uniqueIds = new Set();
    let count = 0;
    
    // Count unique exercises until we hit a duplicate
    for (let i = 0; i < exercises.length; i++) {
      const exerciseId = exercises[i].id;
      if (!uniqueIds.has(exerciseId)) {
        uniqueIds.add(exerciseId);
        count++;
      } else {
        console.log(`🔍 [useRecyclingPhase] Found first duplicate at index ${i}, stopping count`);
        break;
      }
    }
    
    console.log(`🔍 [useRecyclingPhase] Total original exercises: ${count}`);
    return count === 0 ? exercises.length : count;
  }, [exercises]);
  
  // Determine if we're in the recycling phase (now just for UI/tracking purposes)
  const isInRecyclingPhase = useMemo(() => {
    if (!currentExerciseId) {
      return false;
    }
    
    // If totalOriginalExercises is 0, we're already in recycling phase
    if (totalOriginalExercises === 0) {
      return true;
    }
    
    // Check if this word is in the review list
    const isInReviewList = wordsToReview.includes(currentExerciseId);
    
    // Check for global tracking flag
    const globalRecyclingFlag = (window as any).__HAD_RECYCLED_WORDS__ === true;
    
    return isInReviewList || globalRecyclingFlag;
  }, [currentExerciseId, totalOriginalExercises, wordsToReview]);
  
  return {
    isInRecyclingPhase,
    calculateTotalOriginalExercises
  };
};
